import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useFormikContext } from "formik";
import { styled } from "@mui/material";
import { AddPhotoAlternate } from "@mui/icons-material";
import {
  useActionMedia,
  useDeleteActionMedia,
  useUpdateActionMedia,
  useUploadActionMedia,
} from "stores";
import { useActionModalGetContext } from "context/ActionModalProvider";
import {
  Button,
  ConfirmationDialog,
  MediaLibrary,
  Title,
  UploadWrapper,
} from "components";
import NoContentFound from "components/NoContentFound/NoContentFound";
import { useToggle } from "hooks/useToggle";

const MediaSectionStyled = styled("div")({
  width: "100%",
  "& .media-title": {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    "& .upload-button": {
      marginLeft: "1rem",
      minWidth: 0,
    },
  },
});

const NoContentStyled = styled(NoContentFound)({
  padding: "3rem",
});

const ActionMediaSection = ({ isAllowed }) => {
  const { t } = useTranslation();
  const { fieldId } = useParams();
  const { action, fieldInfo } = useActionModalGetContext();
  const { setFieldValue, values } = useFormikContext();
  const actionId = action.action?.id;
  const cropId = fieldInfo.varietyRead?.cropId;

  const { isOpen, open, close } = useToggle();
  const [imageToDelete, setImageToDelete] = useState();

  const {
    media,
    addToMediaState,
    removeFromMediaState,
    updateMediaState,
    fetchState,
  } = useActionMedia({
    actionId,
    fieldId,
  });

  const { deleteMedia, fetchState: deleteFetchState } = useDeleteActionMedia({
    fieldId,
  });
  const { uploadMedia, fetchState: uploadFetchState } = useUploadActionMedia({
    actionId,
    fieldId,
  });
  const { patchMedia } = useUpdateActionMedia({
    actionId,
    fieldId,
  });

  const handleDelete = useCallback(async () => {
    await deleteMedia(imageToDelete?.id);

    removeFromMediaState(imageToDelete?.id);
    close();
  }, [deleteMedia, close, removeFromMediaState, imageToDelete]);

  const openDeleteConfirmation = useCallback(
    (file) => {
      setImageToDelete(file);
      open();
    },
    [open],
  );

  const handleUpload = useCallback(
    async (file) => {
      const payload = {
        file,
        cropId,
      };

      const data = await uploadMedia(payload);

      addToMediaState(data);

      const isExistingAction = Boolean(actionId);

      if (!isExistingAction) {
        const existingAttachments = values?.attachments ?? [];
        setFieldValue("attachments", [data, ...existingAttachments]);
      }
    },
    [cropId, uploadMedia, addToMediaState, actionId, setFieldValue, values],
  );

  const handlePatch = useCallback(
    async (id, title) => {
      const payload = {
        fileName: title,
      };

      let data = { fileName: title };

      const isExistingAction = Boolean(actionId);
      if (isExistingAction) {
        data = await patchMedia({ fileId: id, payload });
      }
      updateMediaState(id, data);

      if (!isExistingAction) {
        setFieldValue(
          "attachments",
          values?.attachments.map((item) =>
            item.id === id ? { ...item, fileName: title, title } : item,
          ),
        );
      }
    },
    [
      actionId,
      values?.attachments,
      patchMedia,
      setFieldValue,
      updateMediaState,
    ],
  );

  return (
    <>
      <MediaSectionStyled>
        <div className="media-title">
          <Title>{t("ActionModal.Images.Title")}</Title>
          {fetchState.isSuccess && (
            <Title sx={{ fontSize: "16px" }}>{`(${media?.length})`}</Title>
          )}
          <UploadWrapper maxSizeInMegaBytes={12} onFileChange={handleUpload}>
            {(handleClick) => (
              <Button
                variant="outlined"
                corners="square"
                isDisabled={!isAllowed || uploadFetchState.isLoading}
                className="upload-button"
                onClick={handleClick}
              >
                <AddPhotoAlternate fontSize="small" />
              </Button>
            )}
          </UploadWrapper>
        </div>
        <div>
          {isAllowed ? (
            <>
              {fetchState.isLoading && <MediaLibrary.Skeleton />}
              {fetchState.isError && (
                <NoContentStyled>
                  {t("ActionModal.Images.ErrorFetching")}
                </NoContentStyled>
              )}
              {fetchState.isSuccess &&
                (media?.length === 0 ? (
                  <NoContentStyled>
                    {t("ActionModal.Images.NoImages")}
                  </NoContentStyled>
                ) : (
                  <MediaLibrary
                    mediaFiles={media}
                    isAddingNew={uploadFetchState.isLoading}
                    onDelete={openDeleteConfirmation}
                    onSaveChanges={handlePatch}
                  />
                ))}
            </>
          ) : (
            <NoContentStyled>
              {t("ActionModal.Images.NotAllowed")}
            </NoContentStyled>
          )}
        </div>
      </MediaSectionStyled>
      <ConfirmationDialog
        isConfirmLoading={deleteFetchState.isLoading}
        isOpen={isOpen}
        onCancel={close}
        title={t("ActionModal.Images.DeleteDialog.Title")}
        contentText={t("ActionModal.Images.DeleteDialog.Content", {
          fileName: imageToDelete?.title,
        })}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default ActionMediaSection;
